import React, { FC, useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { Modal, Button, Progress, Alert, Skeleton } from "../../../components";
import { useStore } from "effector-react";
import { $selectedPersonTypes } from "../../../store/selected/selectedPersonTypes";
import { AvailabilityCard, PersonTypesCard } from "../../../cards";
import cloneDeep from "lodash/cloneDeep";
import { useTranslation } from "react-i18next";
import {
  $selectedPackages,
  selectedPackagesApi,
} from "../../../store/selected/selectedPackages";
import { PackagesEndpoint } from "../../../store/endpoints/packages";
import { useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { DndProvider } from "react-dnd";
import stl from "./EditPackage.module.scss";
import {
  $selectedDates,
  $selectedPreferredDays,
} from "../../../store/selected/selectedDateRange";
import { $selectedLocation } from "../../../store/selected/selectedLocations";
import {
  PersonType,
  PackageAvailability,
  Package,
} from "../../../store/models";
import cn from "classnames";
import { AppConfig } from "../../../config";
import { BsClock, BsPencil } from "react-icons/bs";

export const editPackage = (package_: Package): void => {
  const container = document.createElement("div");
  document.body.appendChild(container);

  const unmount = () => {
    const unmountResult = ReactDOM.unmountComponentAtNode(container);
    if (unmountResult && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  setTimeout(() => {
    ReactDOM.render(
      <EditPackageModal package={package_} onClose={unmount} />,
      container
    );
  });
};

interface Props {
  package: Package;
  onClose: () => void;
}
const EditPackageModal: FC<Props> = ({ onClose, package: package_ }) => {
  const { t } = useTranslation(AppConfig.translationFileNames.step_Main);
  // START Subscriptions to outer data
  const selectedDates = useStore($selectedDates);
  const selectedPreferredDays = useStore($selectedPreferredDays);
  const selectedLocation = useStore($selectedLocation);
  const { data: packages } = useStore(PackagesEndpoint.list.store);
  const selectedPackages = useStore($selectedPackages);
  const selectedPersonTypes = useStore($selectedPersonTypes);
  const { data: availabilitySlotsReceived, pending } = useStore(
    PackagesEndpoint.timeSlots.store
  );
  const { pending: reservePending, error: reserveError } = useStore(
    PackagesEndpoint.reserve.store
  );
  const { pending: cancelReservationPending } = useStore(
    PackagesEndpoint.cancelReservation.store
  );
  // END Subscriptions to outer data

  // START Local data stores
  const [personTypes, setPersonTypes] = useState<
    (PersonType & { quantity: number })[]
  >([]);
  const [activitiesOrder, setActivitiesOrder] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedPackage, setSelectedPackage] = useState<
    typeof selectedPackages[0] | null
  >(null);
  const [
    availabilitySlot,
    setAvailabilitySlot,
  ] = useState<PackageAvailability | null>(null);
  const isAvailabilitySelected = !!availabilitySlot;
  const [availabilitySlots, setAvailabilitySlots] = useState<
    PackageAvailability[]
  >([]);
  // END Local data stores

  useEffect(() => {
    if (availabilitySlotsReceived) {
      setAvailabilitySlots(availabilitySlotsReceived);
      if (availabilitySlotsReceived.length === 1) {
        setAvailabilitySlot(availabilitySlotsReceived[0]);
      }
    }
  }, [availabilitySlotsReceived]);

  const resetAvailability = () => {
    PackagesEndpoint.timeSlots.cancel();
    setAvailabilitySlots([]);
    setAvailabilitySlot(null);
    PackagesEndpoint.timeSlots.reset();
  };

  const onPersonTypeChange = (v: { id: number; qty: number; name: string }) => {
    const storeCloned = cloneDeep(personTypes);

    const item = storeCloned.find((i) => i.id === v.id);
    if (item) item.quantity = v.qty;

    setPersonTypes(storeCloned);
    resetAvailability();
  };

  const onActivitiesOrderChange = (draggedId: number, targetId: number) => {
    const arr = cloneDeep(activitiesOrder);

    const draggedIdx = arr.findIndex(({ id }) => id === draggedId);
    const targetIdx = arr.findIndex(({ id }) => id === targetId);

    // swaps elements in arr
    if (~draggedIdx && ~targetIdx) {
      [arr[draggedIdx], arr[targetIdx]] = [arr[targetIdx], arr[draggedIdx]];
      resetAvailability();
    }

    setActivitiesOrder(arr);
  };

  useEffect(() => {
    const selectedPackage = selectedPackages.find(
      ({ entity: { id } }) => id === package_.id
    );
    if (selectedPackage) {
      const mergedPersonTypes = [...selectedPersonTypes].map((item) => {
        const selectedPersonType = selectedPackage.personTypes.find(
          ({ id }) => id === item.id
        );
        return {
          ...item,
          quantity: selectedPersonType ? selectedPersonType.quantity : 0,
        };
      });

      setSelectedPackage(selectedPackage);
      setPersonTypes(mergedPersonTypes);
      setActivitiesOrder(selectedPackage.activitiesOrder);
      setAvailabilitySlot(selectedPackage.availabilitySlot);
      setAvailabilitySlots(selectedPackage.availabilitySlots);
    } else {
      setPersonTypes(cloneDeep(selectedPersonTypes));

      const packageData = packages?.data.find(({ id }) => id === package_.id);
      if (packageData) {
        setActivitiesOrder(packageData.activities);
      }
    }

    return () => {
      PackagesEndpoint.timeSlots.reset();
      PackagesEndpoint.reserve.reset();
      PackagesEndpoint.cancelReservation.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = async (): Promise<void> => {
    if (availabilitySlot) {
      if (selectedPackage) {
        await PackagesEndpoint.cancelReservation.call(
          selectedPackage.instancesIds
        );
      }

      const instancesIds = await PackagesEndpoint.reserve.call({
        package: package_,
        personTypes: personTypes,
        availability: availabilitySlot,
      });

      selectedPackagesApi.save({
        entity: package_,
        personTypes: personTypes.filter(({ quantity }) => quantity > 0),
        activitiesOrder,
        availabilitySlot,
        availabilitySlots,
        instancesIds,
      });

      onClose();
    }
  };

  const searchAvailableTimeSlots = async () => {
    if (selectedDates && selectedLocation) {
      await PackagesEndpoint.timeSlots.call({
        id: package_.id,
        locationId: selectedLocation.id,
        dates: selectedDates,
        weekdays: selectedPreferredDays,
        personTypes: personTypes.filter(({ quantity }) => quantity > 0),
        activitiesOrder,
      });
    }
  };

  return (
    <Modal
      visible={true}
      onClose={onClose}
      getContainer={false}
      destroyOnClose={false}
      width={500}
    >
      {(reservePending || cancelReservationPending) && (
        <Progress fill style={{ borderRadius: 15 }}>
          {t("packageModal.reserving")}
        </Progress>
      )}

      <h2 style={{ marginTop: 0 }}>{t("packageModal.Details")}</h2>

      <div style={isAvailabilitySelected ? { opacity: 0.5 } : {}}>
        <h4 style={{ fontWeight: 500 }}>{t("packageModal.Activities")}</h4>
        <DndProvider
          backend={AppConfig.isTouchDevice ? TouchBackend : HTML5Backend}
        >
          {activitiesOrder.map(({ id, name }, idx) => (
            <Activity
              key={name}
              index={idx + 1}
              id={id}
              name={name}
              moveCard={onActivitiesOrderChange}
              draggable={
                !isAvailabilitySelected &&
                activitiesOrder.length > 1 &&
                package_.customer_can_change_order
              }
            />
          ))}
        </DndProvider>

        <h4 style={{ fontWeight: 500 }}>{t("packageModal.PersonTypes")}</h4>
        <div>
          <PersonTypesCard
            disabled={isAvailabilitySelected}
            style={{ marginBottom: 30 }}
            value={personTypes}
            onChange={onPersonTypeChange}
            min={1}
            maxValue={selectedPersonTypes}
            fullwidth
          />
        </div>
      </div>

      <div>
        {!isAvailabilitySelected ? (
          <Button
            icon={<BsClock />}
            // size={"small"}
            variant={"action"}
            color={"primary"}
            disabled={pending}
            onClick={searchAvailableTimeSlots}
          >
            {t("packageModal.checkAvailability")}
          </Button>
        ) : (
          <Button
            icon={<BsPencil />}
            // size={"small"}
            variant={"action"}
            color={"edit"}
            onClick={() => setAvailabilitySlot(null)}
          >
            {t("packageModal.edit")}
          </Button>
        )}
      </div>

      {(pending || availabilitySlots.length > 0) && (
        <h4 style={{ fontWeight: 500 }}>{t("packageModal.TimeSlots")}</h4>
      )}

      {pending && (
        <Skeleton height={150} showIcon>
          {t("packageModal.retrievingTimeSlots")}
        </Skeleton>
      )}
      {!pending && availabilitySlots.length > 0 && (
        <AvailabilityCard
          data={availabilitySlots}
          selected={availabilitySlot}
          onSelect={setAvailabilitySlot as any}
        />
      )}

      {!pending && availabilitySlotsReceived?.length === 0 && (
        <Alert variant={"error"} style={{ marginTop: 15 }}>
          {t("packageModal.noResults")}
        </Alert>
      )}

      {reserveError && (
        <Alert variant={"error"} style={{ marginTop: 15 }}>
          {t("packageModal.booked")}
        </Alert>
      )}

      {isAvailabilitySelected && (
        <Button
          style={{ marginTop: 20 }}
          variant={"primary"}
          onClick={apply}
          disabled={selectedPackage?.availabilitySlot === availabilitySlot}
        >
          {t("packageModal.apply")}
        </Button>
      )}
    </Modal>
  );
};

// ACTIVITY
interface ActivityProps {
  id: any;
  name: string;
  index: number;
  draggable?: boolean;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
}
interface DragItem {
  id: number;
  type: string;
}
export const Activity: FC<ActivityProps> = ({
  id,
  name,
  index,
  moveCard,
  draggable,
}) => {
  const DRAG_KEY = "ACTIVITY_ORDER";
  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop<DragItem, any, any>({
    accept: DRAG_KEY,
    hover(item) {
      if (!ref.current) return;
      const dragId = item.id;
      const hoverId = id;

      if (dragId === hoverId) return;

      moveCard(dragId, hoverId);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: { type: DRAG_KEY, id },
    canDrag: draggable,
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;

  drag(drop(ref));

  return (
    <div
      className={cn(stl.activity, { [stl.activityNotDraggable]: !draggable })}
      ref={ref}
      style={{ opacity }}
    >
      <i>{index}</i>
      {name}
    </div>
  );
};
